import { useEffect, useState } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/shared/components';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import useToast from '@/shared/hooks/useToast';
import { InformationIcon } from '@/shared/icons/large';
import { getChainflipToken, type RpcOrder } from '@/shared/utils';
import { useStateChainAccount } from '../hooks/useStateChainAccount';

const CloseAllOrdersModal = ({
  open,
  onClose,
  ordersToClose,
  asset,
}: {
  open: boolean;
  onClose: () => void;
  ordersToClose: RpcOrder[];
  asset?: ChainflipAsset;
}) => {
  const { sendExtrinsic } = useStateChainAccount();
  const extrinsic = 'cancel-orders-batch' as const;
  const toast = useToast(extrinsic);
  const [orderCount, setOrderCount] = useState(ordersToClose.length);

  const { mutate: onConfirm, isPending } = useMutation({
    mutationFn: async (_: React.MouseEvent<HTMLButtonElement>) => {
      toast.loading('Submitting transaction to the State Chain...');
      await sendExtrinsic(extrinsic, ordersToClose);
    },
    onSuccess() {
      toast.success(`Orders closed successfully`);
      onClose();
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  const token = asset ? getChainflipToken(asset) : undefined;

  const assetInfo = token ? ` ${token.symbol} (${token.chain.chainflipId})` : '';

  useEffect(() => {
    if (open) setOrderCount(ordersToClose.length);
  }, [open]);

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => {
        setOrderCount(0);
      }}
    >
      <div className="bg-holy-radial-gray-3-60 relative min-w-[22rem] rounded-md border border-cf-gray-4 backdrop-blur-[6px]">
        <div className="flex h-full flex-col justify-between gap-6 p-5">
          <div className="flex flex-col gap-4">
            <span className="cf-gray-gradient font-aeonikMedium text-20">
              Close all open{assetInfo} orders?
            </span>
            <div className="max-w-xs text-14 text-cf-light-2">
              {orderCount} open{assetInfo} order
              {orderCount === 1 ? '' : 's'} will be closed. This can&apos;t be undone.
            </div>
            <div className="flex flex-row items-center gap-1 text-14 text-cf-blue-2">
              <InformationIcon />
              <span>Boost positions are not affected</span>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end justify-self-end">
            <Button type="secondary-standard" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary-danger"
              size="small"
              onClick={onConfirm}
              loading={isPending && 'iconOnly'}
            >
              Close all
            </Button>
          </div>
        </div>
      </div>
    </DashboardModal>
  );
};

export default CloseAllOrdersModal;
